import { Serie } from "@nivo/line";
import {
    IOEECalculationSummary,
    IQualityOEECalculationSummary,
} from "components/HMIUptimeDowntimeBar/HMIUptimeDowntimeBarInterfaces";
import { parseIntervalStartMs } from "utilities/qualityIntervals";

const ONE_HOUR_MS = 3_600_000;

/**
 * Formats a UTC timestamp as "HH:00" in the given timezone.
 */
export function formatHourLabel(ms: number, timezone: string): string {
    const date = new Date(ms);
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).formatToParts(date);
    const hour = parts.find((p) => p.type === "hour")!.value;
    return `${hour}:00`;
}

/** Floors an epoch-ms timestamp to its UTC hour boundary. */
const floorToHourUtc = (ms: number): number => ms - (ms % ONE_HOUR_MS);

/**
 * The shift window a trend chart walks. Any missing field yields an empty
 * chart (the shift is not resolved yet). Passed as an object so the three
 * `string | undefined` fields cannot be silently transposed.
 */
export interface ITrendWindow {
    shiftStart: string | undefined;
    shiftEnd: string | undefined;
    timezone: string | undefined;
    /** "Now" in epoch ms; defaults to `Date.now()`. Injectable for deterministic tests. */
    nowMs?: number;
}

export interface IHourCell {
    /** UTC hour boundary the cell starts at. */
    hourStartMs: number;
    /** Hour-END label ("HH:00" in the mill timezone), matching the existing trend convention. */
    label: string;
    /** Seconds of this hour that fall inside the shift — `min(hourEnd, shiftEnd) − max(hourStart, shiftStart)`. */
    effectiveSeconds: number;
}

/**
 * Builds the hourly grid a shift trend chart walks: from the shift start
 * floored to its hour boundary up to `min(shiftEnd, currentHourStart)`.
 * The in-progress hour is excluded (summaries for it are still moving).
 * Each cell carries its shift-clipped duration, mirroring the local-server's
 * RunTimeCalculator clipping, so per-hour ratios can use the true interval
 * length on partial first/last hours.
 */
export function buildShiftHourGrid({ shiftStart, shiftEnd, timezone, nowMs = Date.now() }: ITrendWindow): IHourCell[] {
    if (!shiftStart || !shiftEnd || !timezone) return [];

    const currentHourMs = floorToHourUtc(nowMs);
    const shiftStartMs = new Date(shiftStart).getTime();
    const shiftEndMs = new Date(shiftEnd).getTime();
    const gridStartMs = floorToHourUtc(shiftStartMs);
    const gridEndMs = Math.min(shiftEndMs, currentHourMs);

    if (gridStartMs >= gridEndMs) return [];

    const cells: IHourCell[] = [];
    for (let ms = gridStartMs; ms < gridEndMs; ms += ONE_HOUR_MS) {
        cells.push({
            hourStartMs: ms,
            label: formatHourLabel(ms + ONE_HOUR_MS, timezone),
            effectiveSeconds: (Math.min(ms + ONE_HOUR_MS, shiftEndMs) - Math.max(ms, shiftStartMs)) / 1000,
        });
    }
    return cells;
}

/**
 * Indexes summary rows by their `interval_start` epoch ms, dropping rows at
 * or past `cutoffMs` (the in-progress hour). Uses `parseIntervalStartMs` so
 * ISO (local-server) and "YYYY-MM-DD HH:MM:SS" (cloud) rows key uniformly.
 */
export function mapRecordsByInterval<T extends { interval_start: string }>(
    records: T[] | undefined,
    cutoffMs: number,
): Map<number, T> {
    const byInterval = new Map<number, T>();
    if (records) {
        for (const r of records) {
            const ms = parseIntervalStartMs(r.interval_start);
            if (ms < cutoffMs) {
                byInterval.set(ms, r);
            }
        }
    }
    return byInterval;
}

/**
 * Walks the shift hour grid and builds a single Nivo series from `valueAt`,
 * which receives each cell plus a lookup of summary rows keyed by hour start
 * (in-progress hour already excluded). Intervals it maps to `null` render as
 * gaps in the line.
 */
function buildHourlySeries<T extends { interval_start: string }>(
    records: T[] | undefined,
    window: ITrendWindow,
    valueAt: (cell: IHourCell, byInterval: Map<number, T>) => number | null,
): Serie[] {
    const nowMs = window.nowMs ?? Date.now();
    const grid = buildShiftHourGrid({ ...window, nowMs });
    if (grid.length === 0) return [];

    const byInterval = mapRecordsByInterval(records, floorToHourUtc(nowMs));
    const dataPoints = grid.map((cell) => ({ x: cell.label, y: valueAt(cell, byInterval) }));
    return [{ id: "trend", data: dataPoints }];
}

/**
 * Plots one ratio field per hour as a whole percent. Hours with no row, or
 * whose ratio is null, are gaps.
 */
function buildRatioTrendData<T extends { interval_start: string }>(
    records: T[] | undefined,
    pickRatio: (record: T) => number | null,
    window: ITrendWindow,
): Serie[] {
    return buildHourlySeries(records, window, (cell, byInterval) => {
        const record = byInterval.get(cell.hourStartMs);
        const ratio = record ? pickRatio(record) : null;
        return ratio != null ? Math.round(ratio * 100) : null;
    });
}

/**
 * Performance trend: the hour's `performance_ratio`. A fully-down hour
 * (null ratio) is a gap — downtime is Availability's metric, not Performance's.
 */
export function buildPerformanceTrendData(
    records: IOEECalculationSummary[] | undefined,
    window: ITrendWindow,
): Serie[] {
    return buildRatioTrendData(records, (r) => r.performance_ratio, window);
}

/**
 * Quality trend: the hour's `quality_ratio`, same grid and gap rules as
 * the performance trend.
 */
export function buildQualityTrendData(
    records: IQualityOEECalculationSummary[] | undefined,
    window: ITrendWindow,
): Serie[] {
    return buildRatioTrendData(records, (r) => r.quality_ratio, window);
}

export interface IOeeTrendInput extends ITrendWindow {
    perfRecords: IOEECalculationSummary[] | undefined;
    /**
     * Pass `undefined` when quality recording is disabled for the machine
     * center: quality then drops out of the product (factor 1). The caller
     * owns that decision so there is a single source of truth for it.
     */
    qualityRecords: IQualityOEECalculationSummary[] | undefined;
}

/**
 * Overall OEE trend — same hourly grid, where each point is
 * availability × performance × quality for that hour:
 * - hourly availability = run_time_seconds / effectiveSeconds (clamped ≤ 1;
 *   availability over 100% is never legitimate, unlike performance),
 * - performance = the hour's performance_ratio (unclamped, can exceed 1),
 * - quality = the hour's quality_ratio when one exists; otherwise 1.0
 *   (quality disabled → records withheld by the caller → not part of the
 *   calculation; enabled but not yet measured → assumed 100%).
 *
 * Unlike the Performance trend, a fully-down hour (row present with zero run
 * time — the local-server nulls the ratio in that case) is plotted as 0, not
 * gapped: downtime is part of OEE, and hiding the worst hours would make the
 * trend read better than reality. A point is null (gap) only when the hour
 * has no performance row at all, or its ratio is null with run time > 0
 * (not calculable, e.g. no ideal value).
 */
export function buildOeeTrendData({ perfRecords, qualityRecords, ...window }: IOeeTrendInput): Serie[] {
    const nowMs = window.nowMs ?? Date.now();
    const qualityByInterval = mapRecordsByInterval(qualityRecords, floorToHourUtc(nowMs));

    return buildHourlySeries(perfRecords, { ...window, nowMs }, (cell, perfByInterval) => {
        const perfRecord = perfByInterval.get(cell.hourStartMs);
        if (!perfRecord || cell.effectiveSeconds <= 0) return null;
        if (perfRecord.run_time_seconds <= 0) return 0;
        if (perfRecord.performance_ratio == null) return null;

        const qualityRatio = qualityByInterval.get(cell.hourStartMs)?.quality_ratio;
        const availability = Math.min(1, perfRecord.run_time_seconds / cell.effectiveSeconds);
        const qualityFactor = qualityRatio != null ? qualityRatio : 1;
        return Math.round(availability * perfRecord.performance_ratio * qualityFactor * 100);
    });
}
